import type { DocIndex, LibraryDocument, ChatMessage } from '@hump/shared-types';
import { getParser, sourceTypeFromFilename, UrlParser } from '@hump/hump-doc';
import { getStorage } from '../storage/factory';
import { libraryRepository } from './index';
import { bffClient } from '../api/bff-client';

/** 将 ArrayBuffer 转为 string（文本类来源需要） */
function toString(content: ArrayBuffer | string): string {
  return typeof content === 'string'
    ? content
    : new TextDecoder().decode(content);
}

/** 生成唯一 doc ID */
function generateDocId(): string {
  return 'doc_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/** 导入结果 */
export interface ImportResult {
  docId: string;
  title: string;
  blockCount: number;
}

export const docRepository = {
  /**
   * 导入文件：留存原始文件 → 解析 → 创建 Hump Doc → 写入 Storage → 注册到 Library
   *
   * 注意：pdfjs 的 getDocument 会 transfer（移动）ArrayBuffer 到 worker，
   * 导致原始 buffer 变成 detached。必须先复制一份再传给 parser。
   */
  async importFile(filename: string, content: ArrayBuffer | string): Promise<ImportResult> {
    const sourceType = sourceTypeFromFilename(filename);
    if (!sourceType) {
      throw new Error(`不支持的文件格式: ${filename}`);
    }

    const docId = generateDocId();

    // 1. 先留存原始文件（在 parse 之前，确保数据完整）
    const { set: idbSet } = await import('idb-keyval');
    await idbSet(`source:${docId}`, { filename, content, sourceType });

    // 2. 准备 parser 输入——复制 ArrayBuffer（pdfjs 会 transfer 它）
    // URL 导入：注入后端代理 fetchFn（绕过 CORS）
    const parser = sourceType === 'url'
      ? new UrlParser(async (url: string) => {
          const result = await bffClient.import.fetchUrl(url);
          return result.html;
        })
      : getParser(sourceType);
    let parseInput: ArrayBuffer | string;
    if (sourceType === 'md') {
      parseInput = toString(content);
    } else if (content instanceof ArrayBuffer) {
      // 复制一份给 parser，原始 content 已存入 idb-keyval
      parseInput = content.slice(0);
    } else {
      parseInput = content;
    }

    const result = await parser.parse(parseInput);

    // 3. 批量写入 blocks（限制并发，避免大量 IndexedDB 事务）
    const batchSize = 10;
    for (const block of result.blocks) {
      block.docId = docId;
    }
    for (let i = 0; i < result.blocks.length; i += batchSize) {
      const batch = result.blocks.slice(i, i + batchSize);
      await Promise.all(batch.map((b) => getStorage().writeBlock(docId, b)));
    }

    // 4. 创建文档索引
    const docIndex: DocIndex = {
      version: '1.0',
      id: docId,
      meta: {
        title: result.meta.title,
        author: result.meta.author,
        sourceType,
        source: filename,
        importedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        progress: 0,
        lang: result.meta.lang,
        tags: [],
      },
      blockIds: result.blocks.map((b) => b.id),
    };
    await getStorage().writeDocIndex(docIndex);

    // 5. 注册到 library
    const libDoc: LibraryDocument = {
      id: docId,
      title: result.meta.title,
      sourceType,
      addedAt: new Date().toISOString(),
      tags: [],
      progress: 0,
    };
    await libraryRepository.addDocument(libDoc);

    return { docId, title: result.meta.title, blockCount: result.blocks.length };
  },

  /** 读取文档索引 */
  async getDocIndex(docId: string): Promise<DocIndex> {
    return getStorage().readDocIndex(docId);
  },

  /** 读取文档全部 blocks */
  async getBlocks(docId: string) {
    const index = await getStorage().readDocIndex(docId);
    const blocks = await Promise.all(
      index.blockIds.map((id) => getStorage().readBlock(docId, id))
    );
    return blocks;
  },

  /** 删除文档（Storage + Library + 孤儿清理 + 原始文件） */
  async deleteDoc(docId: string): Promise<void> {
    await getStorage().deleteDoc(docId);
    await libraryRepository.removeDocument(docId);
    const { del: idbDel } = await import('idb-keyval');
    await idbDel(`source:${docId}`);
  },

  /** 读取文档对话历史 */
  async getChat(docId: string): Promise<ChatMessage[]> {
    try {
      const { get: idbGet } = await import('idb-keyval');
      const chat = await idbGet(`chat:${docId}`);
      return chat?.messages ?? [];
    } catch {
      return [];
    }
  },

  /** 保存文档对话历史 */
  async saveChat(docId: string, messages: ChatMessage[]): Promise<void> {
    const { set: idbSet } = await import('idb-keyval');
    await idbSet(`chat:${docId}`, { docId, messages });
  },
};
